
import React, { useEffect, useState } from "react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import { useToast } from "../hooks/useToast";
import { getVehicles } from "../services/api";

interface VehicleData {
  id: number;
  vehicleNo: string;
  ownerName: string;
  balance: number;
}

const WriteOffsImproved: React.FC = () => {
  const [vehicles, setVehicles] = useState<VehicleData[]>([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const { showToast, ToastContainer } = useToast();

  const fetchVehicles = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getVehicles();
      setVehicles(data);
      showToast("Vehicles fetched for write-off", "success");
    } catch (err: any) {
      setError(err?.message || "Failed to fetch vehicles");
      showToast(err?.message || "Failed to fetch vehicles", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicles();
    // eslint-disable-next-line
  }, []);

  const handleSearch = () => {
    // No API call, just filter locally
    // If you want to search via API, replace this logic
    // For now, just setSearchText is enough
  };

  const filteredVehicles = vehicles.filter((v) =>
    v.vehicleNo.toLowerCase().includes(searchText.toLowerCase())
  );

  const toggleRow = (id: number) => {
    setExpandedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 w-full">
      <ToastContainer />
      <Card className="w-full p-6">
        <div className="flex items-center mb-4">
          <Button color="secondary" onClick={() => window.history.back()} className="mr-4">
            Back
          </Button>
          <h1 className="text-2xl font-bold text-red-700">Create Write Off</h1>
        </div>
        <div className="flex flex-col md:flex-row md:items-center gap-2 mb-4">
          <input
            type="text"
            placeholder="Search by vehicle number"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="border px-3 py-2 rounded w-full md:w-64"
          />
          <Button color="primary" onClick={handleSearch}>
            Search
          </Button>
          <Button onClick={fetchVehicles} color="primary">
            Refresh
          </Button>
        </div>
        {loading && <LoadingSpinner />}
        {error && <div className="text-red-600 mb-2">{error}</div>}
        {/* Desktop Table */}
        <div className="hidden md:block bg-white shadow rounded-lg overflow-x-auto mt-4">
          <table className="w-full border-collapse border">
            <thead className="bg-gray-100">
              <tr>
                <th className="border px-4 py-2 text-left">Vehicle No</th>
                <th className="border px-4 py-2 text-left">Owner</th>
                <th className="border px-4 py-2 text-left">Balance</th>
              </tr>
            </thead>
            <tbody>
              {filteredVehicles.map((v) => {
                const isExpanded = expandedRows.includes(v.id);
                return (
                  <React.Fragment key={v.id}>
                    <tr
                      className="border-b hover:bg-red-50 cursor-pointer transition"
                      onClick={() => toggleRow(v.id)}
                    >
                      <td className="border px-4 py-2">{v.vehicleNo}</td>
                      <td className="border px-4 py-2">{v.ownerName}</td>
                      <td className="border px-4 py-2">₹{v.balance || 0}</td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-gray-50 border-b">
                        <td colSpan={3} className="px-4 py-2">
                          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2">
                            <div>
                              <p>
                                <strong>Vehicle No:</strong> {v.vehicleNo}
                              </p>
                              <p>
                                <strong>Owner:</strong> {v.ownerName}
                              </p>
                              <p>
                                <strong>Balance:</strong> ₹{v.balance || 0}
                              </p>
                            </div>
                            <Button
                              color="danger"
                              className="mt-2 md:mt-0"
                              onClick={() => {/* Add write-off logic here */}}
                            >
                              Write Off
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {filteredVehicles.length === 0 && (
                <tr>
                  <td colSpan={3} className="text-center py-4 text-gray-500">
                    No vehicles found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Mobile Collapsible Cards */}
        <div className="md:hidden flex flex-col gap-3">
          {filteredVehicles.map((v) => {
            const isExpanded = expandedRows.includes(v.id);
            return (
              <div
                key={v.id}
                className="bg-white shadow rounded-lg p-4 cursor-pointer"
                onClick={() => toggleRow(v.id)}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-semibold text-gray-800">{v.vehicleNo}</p>
                    <p className="text-gray-600">{v.ownerName}</p>
                  </div>
                  <div className="text-gray-500">
                    ₹{v.balance || 0} <span>{isExpanded ? "▲" : "▼"}</span>
                  </div>
                </div>
                {isExpanded && (
                  <div className="mt-2 text-gray-700 border-t pt-2 space-y-2">
                    <p>
                      <strong>Vehicle No:</strong> {v.vehicleNo}
                    </p>
                    <p>
                      <strong>Owner:</strong> {v.ownerName}
                    </p>
                    <p>
                      <strong>Balance:</strong> ₹{v.balance || 0}
                    </p>
                    <Button
                      color="danger"
                      className="w-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        /* Add write-off logic here */
                      }}
                    >
                      Write Off
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
          {filteredVehicles.length === 0 && (
            <p className="text-gray-500 text-center py-4">No vehicles found</p>
          )}
        </div>
      </Card>
    </div>
  );
};

export default WriteOffsImproved;
